import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DisButton from '../components/design-system/DisButton';
import DisCard from '../components/design-system/DisCard';
import DisModal from '../components/design-system/DisModal';
import DisInput from '../components/design-system/DisInput';
import AdminNewsletterSection from '../components/AdminNewsletterSection';
import AdminPushSection from '../components/AdminPushSection';

const AdminPage = ({ onNavigateBack }) => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [demoStats, setDemoStats] = useState(null);
  const [demoLimit, setDemoLimit] = useState(20);
  const [loadingMoreDemos, setLoadingMoreDemos] = useState(false);
  const [userPlatforms, setUserPlatforms] = useState({});
  const [newDiscount, setNewDiscount] = useState({
    code: '',
    type: 'percentage', // 'percentage', 'amount', 'months'
    value: 0,
    validUntil: '',
    usageLimit: 1,
    description: '',
    onePerUser: true
  });
  
  // Fonction de retour (utilise navigate si le callback n'est pas fourni)
  const handleBack = () => {
    if (onNavigateBack) {
      onNavigateBack();
    } else {
      navigate('/chat');
    }
  };

  // Vérifier si l'utilisateur est admin
  const isAdmin = user?.email === 'contact@dismaman.com';

  useEffect(() => {
    if (isAdmin) {
      loadUsers();
      loadDiscountCodes();
      loadDemoStats();
      loadUserPlatforms();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadDiscountCodes = async () => {
    try {
      const response = await api.get('/admin/discount-codes');
      setDiscountCodes(response.data.codes || []);
    } catch (error) {
      console.error('Error loading discount codes:', error);
    }
  };

  const loadDemoStats = async (limit = 20) => {
    try {
      const response = await api.get(`/admin/demo-stats?limit=${limit}`);
      setDemoStats(response.data);
    } catch (error) {
      console.error('Error loading demo stats:', error);
    }
  };

  const loadMoreDemos = async () => {
    setLoadingMoreDemos(true);
    const newLimit = demoLimit + 20;
    setDemoLimit(newLimit);
    await loadDemoStats(newLimit);
    setLoadingMoreDemos(false);
  };

  const loadUserPlatforms = async () => {
    try {
      const response = await api.get('/admin/user-platforms');
      setUserPlatforms(response.data.platforms || {});
    } catch (error) {
      console.error('Error loading user platforms:', error);
    }
  };

  const handleEditUser = (userToEdit) => {
    setSelectedUser({
      ...userToEdit,
      subscription_end_date: userToEdit.subscription_end_date || new Date().toISOString().split('T')[0]
    });
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    try {
      setIsLoading(true);
      
      // Calculer automatiquement is_premium basé sur subscription_plan
      const updatedUser = {
        ...selectedUser,
        is_premium: selectedUser.subscription_plan && selectedUser.subscription_plan !== 'free_trial' && selectedUser.subscription_plan !== 'free'
      };
      
      await api.put(`/admin/users/${selectedUser.id}`, updatedUser);
      await loadUsers();
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Erreur lors de la mise à jour de l\'utilisateur');
    } finally {
      setIsLoading(false);
    }
  };

  // Removed unused admin functions

  const handleResetPassword = async (userEmail) => {
    if (!confirm(`🔐 Envoyer un email de réinitialisation de mot de passe à ${userEmail} ?`)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/api/auth/forgot-password', {
        email: userEmail
      });
      
      alert(`✅ Email de réinitialisation envoyé avec succès !\n\n` +
            `Un email a été envoyé à ${userEmail} avec les instructions pour réinitialiser son mot de passe.`);
      
    } catch (error) {
      console.error('Error sending reset email:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur inconnue';
      alert(`❌ Erreur lors de l'envoi: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userData) => {
    const userEmail = userData.email;
    const userId = userData.id;
    
    // Première confirmation
    if (!confirm(`⚠️ ATTENTION - Suppression de compte\n\n` +
                 `Vous êtes sur le point de supprimer définitivement le compte :\n` +
                 `📧 ${userEmail}\n` +
                 `👤 ${userData.first_name} ${userData.last_name}\n\n` +
                 `Cette action supprimera :\n` +
                 `• Le compte utilisateur\n` +
                 `• Toutes ses conversations\n` +
                 `• Ses abonnements\n` +
                 `• Ses profils enfants\n` +
                 `• Toutes ses données associées\n\n` +
                 `⚠️ CETTE ACTION EST IRRÉVERSIBLE !\n\n` +
                 `Voulez-vous continuer ?`)) {
      return;
    }

    // Deuxième confirmation
    if (!confirm(`🚨 DEUXIÈME CONFIRMATION\n\n` +
                 `Vous confirmez vouloir supprimer définitivement :\n` +
                 `${userEmail}\n\n` +
                 `Toutes les données de cet utilisateur seront perdues à jamais.\n\n` +
                 `Êtes-vous absolument certain ?`)) {
      return;
    }

    // Troisième confirmation avec saisie
    const confirmationText = prompt(`🔥 CONFIRMATION FINALE 🔥\n\n` +
                                   `Pour confirmer la suppression définitive du compte :\n` +
                                   `${userEmail}\n\n` +
                                   `Tapez exactement : SUPPRIMER DÉFINITIVEMENT\n\n` +
                                   `(Cette action est irréversible)`);
    
    if (confirmationText !== 'SUPPRIMER DÉFINITIVEMENT') {
      alert('❌ Suppression annulée - Texte de confirmation incorrect');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      
      alert(`✅ Compte supprimé avec succès !\n\n` +
            `Utilisateur : ${response.data.user_email}\n` +
            `Données supprimées :\n` +
            `• Conversations : ${response.data.deleted_data.conversations}\n` +
            `• Abonnements : ${response.data.deleted_data.subscriptions}\n` +
            `• Modifications enfants : ${response.data.deleted_data.child_modifications}\n` +
            `• Parrainages : ${response.data.deleted_data.referrals}\n\n` +
            `Le compte a été définitivement supprimé.`);
      
      // Recharger la liste des utilisateurs
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur inconnue';
      alert(`❌ Erreur lors de la suppression: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Removed handleSendTrialReminders function

  // Removed handleCleanupDatabase function

  const handleCreateDiscountCode = async () => {
    try {
      setIsLoading(true);
      
      // Mapper les champs frontend vers backend
      const discountData = {
        code: newDiscount.code,
        type: newDiscount.type,
        value: newDiscount.value,
        valid_until: newDiscount.validUntil,
        usage_limit: newDiscount.usageLimit,
        description: newDiscount.description,
        one_per_user: newDiscount.onePerUser
      };
      
      await api.post('/admin/discount-codes', discountData);
      await loadDiscountCodes();
      setShowDiscountModal(false);
      setNewDiscount({
        code: '',
        type: 'percentage',
        value: 0,
        validUntil: '',
        usageLimit: 1,
        description: '',
        onePerUser: true
      });
    } catch (error) {
      console.error('Error creating discount code:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur inconnue lors de la création du code';
      alert(`❌ Erreur lors de la création du code de réduction: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDiscountCode = async (codeId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce code de réduction ?')) {
      try {
        await api.delete(`/admin/discount-codes/${codeId}`);
        await loadDiscountCodes();
      } catch (error) {
        console.error('Error deleting discount code:', error);
        alert('Erreur lors de la suppression du code de réduction');
      }
    }
  };

  // Removed handleFixTrialAccounts function

  const handleFixSubscriptionSync = async () => {
    if (!confirm('🔄 Synchroniser les abonnements utilisateur ?\n\nCette action va :\n• Vérifier les données d\'abonnement de tous les utilisateurs\n• Corriger les incohérences détectées\n• Mettre à jour les statuts premium\n\nContinuer ?')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/admin/fix-subscription-sync');
      
      alert(`✅ Synchronisation terminée !\n\n` +
            `Utilisateurs traités : ${response.data.users_processed}\n` +
            `Corrections appliquées : ${response.data.corrections_applied}\n` +
            `Erreurs détectées : ${response.data.errors_found}\n\n` +
            `Les données utilisateur ont été synchronisées avec leurs abonnements réels.`);
      
      // Recharger la liste des utilisateurs pour voir les changements
      loadUsers();
    } catch (error) {
      console.error('Error fixing subscription sync:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur inconnue';
      alert(`❌ Erreur lors de la synchronisation: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFixSubscriptionDates = async () => {
    if (!confirm('📅 Corriger les dates d\'abonnement incohérentes ?\n\nCette action va :\n• Identifier les plans mensuels avec dates >60 jours\n• Les corriger à 30 jours maximum\n• Synchroniser users et subscriptions\n\nContinuer ?')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/admin/fix-subscription-dates');
      
      alert(`✅ Correction des dates terminée !\n\n` +
            `Abonnements corrigés : ${response.data.fixed_count}\n\n` +
            `${response.data.message}`);
      
      // Recharger la liste des utilisateurs pour voir les changements
      loadUsers();
    } catch (error) {
      console.error('Error fixing subscription dates:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur inconnue';
      alert(`❌ Erreur lors de la correction: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCleanupDatabase = async () => {
    if (!confirm('⚠️ ATTENTION - Nettoyage de la base de données\n\nCette action va supprimer DÉFINITIVEMENT :\n• Tous les comptes utilisateur (sauf les 4 comptes principaux)\n• Toutes leurs conversations\n• Tous leurs abonnements\n• Toutes leurs données\n\nUne sauvegarde sera créée avant suppression.\n\n🚨 CETTE ACTION EST IRRÉVERSIBLE !\n\nVoulez-vous continuer ?')) {
      return;
    }

    // Deuxième confirmation
    const confirmText = prompt('🔥 CONFIRMATION FINALE\n\nPour confirmer le nettoyage complet de la base de données,\ntapez exactement : NETTOYER TOUT\n\n(Cette action est irréversible)');
    
    if (confirmText !== 'NETTOYER TOUT') {
      alert('❌ Nettoyage annulé - Texte de confirmation incorrect');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/admin/cleanup-database');
      
      alert(`✅ Nettoyage terminé !\n\n` +
            `Sauvegarde créée : ${response.data.backup_file}\n` +
            `Comptes supprimés : ${response.data.deleted_users}\n` +
            `Conversations supprimées : ${response.data.deleted_conversations}\n` +
            `Abonnements supprimés : ${response.data.deleted_subscriptions}\n\n` +
            `La base de données a été nettoyée avec succès.`);
      
      // Recharger la liste des utilisateurs
      loadUsers();
    } catch (error) {
      console.error('Error cleaning database:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur inconnue';
      alert(`❌ Erreur lors du nettoyage: ${errorMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center">
        <DisCard className="p-8 text-center">
          <h1 className="text-2xl font-bold text-red-700 mb-4">🚫 Accès refusé</h1>
          <p className="text-red-600 mb-4">Vous n'avez pas les droits d'administrateur.</p>
          <DisButton onClick={handleBack} variant="secondary">
            Retour
          </DisButton>
        </DisCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <DisButton onClick={handleBack} variant="ghost" className="mb-4">
            ← Retour
          </DisButton>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🛠️ Administration</h1>
          <p className="text-gray-600">Interface d'administration - Dis Maman!</p>
        </div>

        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <DisCard className="p-4 text-center bg-blue-50 border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{users.length}</div>
            <div className="text-sm text-blue-700">Utilisateurs totaux</div>
          </DisCard>
          <DisCard className="p-4 text-center bg-yellow-50 border-yellow-200">
            <div className="text-2xl font-bold text-yellow-600">
              {users.filter(u => !u.subscription_plan || u.subscription_plan === 'free_trial' || u.subscription_plan === 'free').length}
            </div>
            <div className="text-sm text-yellow-700">En essai gratuit</div>
          </DisCard>
          <DisCard className="p-4 text-center bg-blue-50 border-blue-200">
            <div className="text-2xl font-bold text-blue-600">
              {users.filter(u => u.subscription_plan?.includes('essential')).length}
            </div>
            <div className="text-sm text-blue-700">Essential</div>
          </DisCard>
          <DisCard className="p-4 text-center bg-green-50 border-green-200">
            <div className="text-2xl font-bold text-green-600">
              {users.filter(u => u.subscription_plan?.includes('family')).length}
            </div>
            <div className="text-sm text-green-700">Family</div>
          </DisCard>
        </div>

        {/* Statistiques Démo Landing Page */}
        {demoStats && (
          <DisCard className="p-6 mb-6" data-testid="demo-stats-card">
            <h2 className="text-xl font-bold text-gray-900 mb-4">📊 Statistiques Démo (Landing Page)</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Questions démo */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                <div className="text-3xl font-bold text-indigo-600" data-testid="demo-questions-total">{demoStats.demo_questions_total}</div>
                <div className="text-sm text-indigo-700 font-medium">Questions démo (total)</div>
                <div className="text-xs text-indigo-500 mt-1">
                  Aujourd'hui : {demoStats.demo_questions_today} · 7j : {demoStats.demo_questions_7d} · 30j : {demoStats.demo_questions_30d}
                </div>
              </div>
              {/* Inscrits via démo */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="text-3xl font-bold text-emerald-600" data-testid="demo-registrations-total">{demoStats.demo_registrations_total}</div>
                <div className="text-sm text-emerald-700 font-medium">Inscrits via démo</div>
                <div className="text-xs text-emerald-500 mt-1">
                  7j : {demoStats.demo_registrations_7d} · 30j : {demoStats.demo_registrations_30d}
                </div>
              </div>
              {/* Taux de conversion */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="text-3xl font-bold text-amber-600" data-testid="demo-conversion-rate">{demoStats.conversion_rate_total}%</div>
                <div className="text-sm text-amber-700 font-medium">Taux de conversion</div>
                <div className="text-xs text-amber-500 mt-1">
                  7j : {demoStats.conversion_rate_7d}% · 30j : {demoStats.conversion_rate_30d}%
                </div>
              </div>
            </div>
            {/* Dernières démos */}
            {demoStats.recent_demos && demoStats.recent_demos.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-2">
                  Dernières questions démo ({demoStats.recent_demos.length}/{demoStats.recent_demos_total || demoStats.demo_questions_total})
                </h3>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {demoStats.recent_demos.map((demo, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-xs bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-gray-400 shrink-0">
                        {new Date(demo.timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className="text-gray-600 font-medium truncate">{demo.child_name}</span>
                      <span className="text-gray-800 truncate flex-1">« {demo.question} »</span>
                    </div>
                  ))}
                </div>
                {demoStats.has_more && (
                  <button
                    onClick={loadMoreDemos}
                    disabled={loadingMoreDemos}
                    className="mt-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                    data-testid="load-more-demos-btn"
                  >
                    {loadingMoreDemos ? 'Chargement...' : 'Voir plus'}
                  </button>
                )}
              </div>
            )}
          </DisCard>
        )}

        {/* Newsletter Section */}
        <AdminNewsletterSection />

        {/* Push Notifications Section */}
        <AdminPushSection />

        {/* Gestion des codes de réduction */}
        <DisCard className="p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">💰 Codes de réduction</h2>
            <DisButton onClick={() => setShowDiscountModal(true)} className="bg-blue-500 hover:bg-blue-600 text-white">
              Nouveau code
            </DisButton>
          </div>
          
          <div className="grid gap-4">
            {discountCodes.map(code => (
              <div key={code.id} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                <div>
                  <div className="font-bold text-lg">{code.code}</div>
                  <div className="text-sm text-gray-600">
                    {code.type === 'percentage' && `${code.value}% de réduction`}
                    {code.type === 'amount' && `${code.value}€ de réduction`}
                    {code.type === 'months' && `${code.value} mois offert${code.value > 1 ? 's' : ''}`}
                    {' • '}Limite: {code.usage_limit} • 
                    Valide jusqu'au {new Date(code.valid_until).toLocaleDateString()}
                    {code.one_per_user && ' • Une fois par utilisateur'}
                  </div>
                  <div className="text-xs text-gray-500">{code.description}</div>
                </div>
                <DisButton
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteDiscountCode(code.id)}
                  className="text-red-600 hover:bg-red-50"
                >
                  🗑️
                </DisButton>
              </div>
            ))}
          </div>
        </DisCard>

        {/* Liste des utilisateurs */}
        <DisCard className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">👥 Utilisateurs</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Nom</th>
                  <th className="text-left p-2">Plan</th>
                  <th className="text-left p-2">Date de renouvellement</th>
                  <th className="text-left p-2">Enfants</th>
                  <th className="text-left p-2">Plateformes</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(userData => (
                  <tr key={userData.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">
                      {userData.email}
                      {userData.is_admin && (
                        <span className="ml-2 px-2 py-1 bg-teal-600 text-white text-xs rounded-full font-bold">
                          👑 ADMIN
                        </span>
                      )}
                    </td>
                    <td className="p-2">{userData.first_name} {userData.last_name}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        userData.subscription_plan === 'free_trial' || userData.subscription_plan === 'free'
                          ? 'bg-yellow-100 text-yellow-700'
                          : userData.subscription_plan?.includes('family')
                          ? 'bg-green-100 text-green-700'
                          : userData.subscription_plan?.includes('essential')
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {userData.subscription_plan || 'free_trial'}
                      </span>
                    </td>
                    <td className="p-2">
                      {userData.subscription_end_date 
                        ? new Date(userData.subscription_end_date).toLocaleDateString()
                        : '-'
                      }
                    </td>
                    <td className="p-2">{userData.children_count || 0}</td>
                    <td className="p-2">
                      <div className="flex gap-1">
                        {(userPlatforms[userData.id] || []).length === 0 ? (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Web</span>
                        ) : (
                          (userPlatforms[userData.id] || []).map(p => (
                            <span key={p} className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              p === 'ios' ? 'bg-gray-900 text-white' : 'bg-green-100 text-green-700'
                            }`} data-testid={`platform-badge-${p}`}>
                              {p === 'ios' ? ' iOS' : ' Android'}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="p-2">
                      <div className="flex gap-2">
                        <DisButton
                          size="sm"
                          onClick={() => handleEditUser(userData)}
                          className="text-xs"
                        >
                          ✏️ Modifier
                        </DisButton>
                        <DisButton
                          size="sm"
                          onClick={() => handleResetPassword(userData.email)}
                          className="text-xs bg-red-500 hover:bg-red-600 text-white border-red-500"
                          loading={isLoading}
                        >
                          🔐 Reset MDP
                        </DisButton>
                        <DisButton
                          size="sm"
                          onClick={() => handleDeleteUser(userData)}
                          className="text-xs bg-red-700 hover:bg-red-800 text-white border-red-700"
                          loading={isLoading}
                          disabled={userData.email === 'contact@dismaman.com'}
                        >
                          🗑️ Supprimer
                        </DisButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DisCard>

        {/* Modal d'édition utilisateur */}
        <DisModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="✏️ Modifier l'utilisateur"
          size="md"
        >
          {selectedUser && (
            <div className="space-y-4">
              <DisInput
                label="Email"
                value={selectedUser.email}
                onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <DisInput
                  label="Prénom"
                  value={selectedUser.first_name}
                  onChange={(e) => setSelectedUser({...selectedUser, first_name: e.target.value})}
                />
                <DisInput
                  label="Nom"
                  value={selectedUser.last_name}
                  onChange={(e) => setSelectedUser({...selectedUser, last_name: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Plan d'abonnement</label>
                <select
                  value={selectedUser.subscription_plan || 'free_trial'}
                  onChange={(e) => setSelectedUser({...selectedUser, subscription_plan: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="free_trial">Essai gratuit</option>
                  <option value="essential_monthly">Essential Mensuel</option>
                  <option value="essential_yearly">Essential Annuel</option>
                  <option value="family_monthly">Famille Mensuel</option>
                  <option value="family_yearly">Famille Annuel</option>
                </select>
              </div>

              <DisInput
                label="Date de fin d'abonnement"
                type="date"
                value={selectedUser.subscription_end_date}
                onChange={(e) => setSelectedUser({...selectedUser, subscription_end_date: e.target.value})}
              />

              <div className="flex justify-end space-x-3 pt-4">
                <DisButton
                  variant="secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Annuler
                </DisButton>
                <DisButton
                  onClick={handleSaveUser}
                  loading={isLoading}
                >
                  Sauvegarder
                </DisButton>
              </div>
            </div>
          )}
        </DisModal>

        {/* Modal création code de réduction */}
        <DisModal
          isOpen={showDiscountModal}
          onClose={() => setShowDiscountModal(false)}
          title="💰 Nouveau code de réduction"
          size="md"
        >
          <div className="space-y-4">
            <DisInput
              label="Code de réduction"
              value={newDiscount.code}
              onChange={(e) => setNewDiscount({...newDiscount, code: e.target.value.toUpperCase()})}
              placeholder="ex: WELCOME20"
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type de réduction</label>
              <select
                value={newDiscount.type}
                onChange={(e) => setNewDiscount({...newDiscount, type: e.target.value, value: 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="percentage">Pourcentage (%)</option>
                <option value="amount">Montant fixe (€)</option>
                <option value="months">Mois offerts</option>
              </select>
            </div>
            
            <DisInput
              label={
                newDiscount.type === 'percentage' ? 'Pourcentage de réduction (%)' :
                newDiscount.type === 'amount' ? 'Montant de réduction (€)' :
                'Nombre de mois offerts'
              }
              type="number"
              value={newDiscount.value}
              onChange={(e) => setNewDiscount({...newDiscount, value: parseFloat(e.target.value) || 0})}
              min={newDiscount.type === 'percentage' ? '1' : '0.01'}
              max={newDiscount.type === 'percentage' ? '100' : undefined}
              step={newDiscount.type === 'amount' ? '0.01' : '1'}
              placeholder={
                newDiscount.type === 'percentage' ? 'ex: 20' :
                newDiscount.type === 'amount' ? 'ex: 5.00' :
                'ex: 1'
              }
            />

            <DisInput
              label="Valide jusqu'au"
              type="date"
              value={newDiscount.validUntil}
              onChange={(e) => setNewDiscount({...newDiscount, validUntil: e.target.value})}
            />

            <DisInput
              label="Nombre total d'utilisations"
              type="number"
              value={newDiscount.usageLimit}
              onChange={(e) => setNewDiscount({...newDiscount, usageLimit: parseInt(e.target.value) || 1})}
              min="1"
              placeholder="ex: 100"
            />

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="onePerUser"
                checked={newDiscount.onePerUser}
                onChange={(e) => setNewDiscount({...newDiscount, onePerUser: e.target.checked})}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <label htmlFor="onePerUser" className="text-sm text-gray-700">
                Une seule utilisation par utilisateur
              </label>
            </div>

            <DisInput
              label="Description"
              value={newDiscount.description}
              onChange={(e) => setNewDiscount({...newDiscount, description: e.target.value})}
              placeholder="Code de bienvenue"
            />

            {/* Aperçu du code */}
            {newDiscount.code && newDiscount.value > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm font-medium text-green-800">Aperçu :</div>
                <div className="text-sm text-green-700">
                  Code <strong>{newDiscount.code}</strong> : 
                  {newDiscount.type === 'percentage' && ` ${newDiscount.value}% de réduction`}
                  {newDiscount.type === 'amount' && ` ${newDiscount.value}€ de réduction`}
                  {newDiscount.type === 'months' && ` ${newDiscount.value} mois offert${newDiscount.value > 1 ? 's' : ''}`}
                  {newDiscount.onePerUser && ' (une fois par utilisateur)'}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <DisButton
                variant="secondary"
                onClick={() => setShowDiscountModal(false)}
              >
                Annuler
              </DisButton>
              <DisButton
                onClick={handleCreateDiscountCode}
                loading={isLoading}
                disabled={!newDiscount.code || !newDiscount.value || newDiscount.value <= 0}
              >
                Créer le code
              </DisButton>
            </div>
          </div>
        </DisModal>
      </div>
    </div>
  );
};

export default AdminPage;